import "react-toastify/dist/ReactToastify.css";
import "./tool-list.css";

import CloseIcon from "@mui/icons-material/Close";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import SaveIcon from "@mui/icons-material/Save";
import SearchIcon from "@mui/icons-material/Search";
import InnerHTML from "dangerously-set-html-content";

import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  Grid,
  IconButton,
  Input,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Slide,
  TextField,
  Typography,
} from "@mui/material";

import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";

import Loader from "../../components/Loader";
import { baseUrl } from "../../variables/variables";
import { updateToolSubmissionStatus } from "./../../adapter/ToolAdapter";
const ITEMS_PER_PAGE = 10;

// "Recently used by you" — remembered per-browser only (localStorage), so it
// reflects each user's own workflow with no backend. Wrapped in try/catch since
// storage can be unavailable (private mode / blocked).
const RECENT_KEY = "recentToolIds";
const RECENT_MAX = 6;
const readRecentIds = () => {
  try {
    const v = JSON.parse(localStorage.getItem(RECENT_KEY));
    return Array.isArray(v) ? v : [];
  } catch (e) {
    return [];
  }
};
const pushRecentId = (id) => {
  try {
    const next = [id, ...readRecentIds().filter((x) => x !== id)].slice(
      0,
      RECENT_MAX
    );
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    return next;
  } catch (e) {
    return readRecentIds();
  }
};

const ToolList = () => {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [toolData, setToolData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const [customTool, setCustomTool] = useState([]);

  const [selectedTool, setSelectedTool] = useState(null);
  const [search, setSearch] = useState("");
  const [recentIds, setRecentIds] = useState(readRecentIds());
  const navigate = useNavigate();
  const { id: routeToolId } = useParams();
  const fetchToolsData = async (tool) => {
    try {
      const response = await axios.get(baseUrl + "/tools");
      setToolData(response.data.data);

      // Filter the tool data based on the type
      const uiPythonBasedTool = response.data.data.find(
        (e) => e.id === tool.id
      );
      if (uiPythonBasedTool) {
        // Call the service to get the HTML content for the UI-Python-based tool
        const htmlContent = await axios.get(
          `${baseUrl}/tools/toolId/${uiPythonBasedTool.id}`
        );
        setCustomTool(htmlContent.data);
      }
      setModalOpen(true);
    } catch (error) {
    }
  };

  useEffect(() => {
    const fetchTools = async () => {
      try {
        const response = await axios.get(baseUrl + "/tools");
        setToolData(response.data.data);
      } catch (error) {
      }
    };
    fetchTools();
  }, []);

  const handleSubmit = async (event, tool) => {
    event.preventDefault();
    setLoading(true);
    setModalOpen(false);
    let uploadFailed = false;
    const sortedInputConfigurations = [...tool.inputConfigurations].sort(
      (a, b) => a.index - b.index
    );

    try {
      const formDataToSend = {
        toolId: tool.id,
        createdBy: localStorage.getItem("userEmail"),
      };

      for (const input of sortedInputConfigurations) {
        const inputTitle = `input${input.index}`;
        if (input.type === "file") {
          formDataToSend[inputTitle] = formData[input.title] ? formData[input.title].name : "";
        } else {
          formDataToSend[inputTitle] = formData[input.title];
        }
      }
      const response = await axios.post(
        baseUrl + "/tool-submissions",
        formDataToSend
      );

      if (response.data.status === "success") {
        for (const input of sortedInputConfigurations) {
          if (input.type === "file") {
            const formDataFile = new FormData();
            formDataFile.append("file", formData[input.title]);
            formDataFile.append("toolSubmissionId", response.data.data.id);
            formDataFile.append("inputIndex", input.index);
            let uploadResponce = await axios.post(
              baseUrl + "/files",
              formDataFile
            );
            if (uploadResponce.data.status !== "success") {
              toast.error("File upload failed:");
              uploadFailed = true;
              break;
            }
          }
        }
      } else {
        toast.error("File upload failed:", response.data.message);
        return;
      }
      const status = uploadFailed ? "failed" : "ready_to_execute";
      const updateResponse = await updateToolSubmissionStatus(
        baseUrl,
        response.data.data.id,
        status
      );
      if (updateResponse.status === "fail") {
      }

      // Show appropriate message based on upload success/failure
      if (uploadFailed) {
        toast.error("File upload failed. Status updated to 'failed'");
      } else {
        toast.success("All files uploaded successfully and ready to execute!");
        setModalOpen(false); // Close the modal after successful submission
        setFormData({}); // Clear the form data here
      }
    } catch (error) {
      toast.error("Error submitting form");
    } finally {
      setLoading(false);
      setFormData({}); // Clear the form data here
    }
  };
  const handleChange = (event, inputType, inputTitle) => {
    if (inputType === "file") {
      const file = event.target.files[0];
      setFormData((prevState) => ({
        ...prevState,
        [inputTitle]: file,
      }));
    } else {
      setFormData((prevState) => ({
        ...prevState,
        [inputTitle]: event.target.value,
      }));
    }
  };
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const renderInputField = (input, index, labelSize = "13px") => {
    switch (input.type) {
      case "file":
        return (
          <FormControl fullWidth key={index} margin="normal">
            <InputLabel shrink style={{ fontSize: "14px" }}>
              {input.title}
            </InputLabel>
            <Input
              id={input.title}
              type="file"
              size="small"
              onChange={(event) => handleChange(event, input.type, input.title)}
              required={input.required}
            />
          </FormControl>
        );
      case "text":
        return (
          <TextField
            fullWidth
            className="card-form-text-input"
            key={index}
            label={input.title}
            placeholder={input.helpText}
            variant="outlined"
            margin="normal"
            onChange={(event) => handleChange(event, input.type, input.title)}
            InputLabelProps={{
              shrink: true,
              style: { fontSize: "15px" },
            }}
            size="small"
            required={input.required}
          />
        );
      case "email":
      case "number":
        return (
          <TextField
            fullWidth
            key={index}
            label={input.title}
            variant="outlined"
            margin="normal"
            type={input.type === "number" ? "number" : "text"}
            onChange={(event) => handleChange(event, input.type, input.title)}
            InputLabelProps={{ shrink: true }}
            size="small"
            required={input.required}
          />
        );
      case "textarea":
        return (
          <textarea
            key={index}
            aria-label={input.title}
            placeholder={input.title}
            style={{ width: "100%", fontSize: labelSize, borderRadius: "5px" }}
            sx={{ borderColor: "#bdb5b5" }}
            // onChange={(event) => handleChange(event, input.type, input.title)}
            required={input.required}
            maxRows={3}
            minRows={3}
          />
        );

        case "select":
          return (
            <FormControl key={index} margin="normal">
              <InputLabel shrink>{input.title}</InputLabel>
              <Select
                label={input.title}
                variant="outlined"
                value={formData[input.title] || ""}
                onChange={(event) => handleChange(event, input.type, input.title)}
                displayEmpty
                sx={{ height: "40px" }}
              >
                <MenuItem value="" disabled>
                  Select {input.title}
                </MenuItem>
                {/* Set predefined options here */}
                {["FC","LRN_br","LRN_cbr","SOA"].map((option, optionIndex) => (
                  <MenuItem key={optionIndex} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
      default:
        return (
          <TextField
            key={index}
            label={input.title}
            variant="outlined"
            margin="normal"
            onChange={(event) => handleChange(event, input.type, input.title)}
            InputLabelProps={{ shrink: true }}
            size="small"
            required={input.required}
          />
        );
    }
  };
  const openToolInModal = async (tool) => {
    setLoading(true);
    setSelectedTool(tool);
    setRecentIds(pushRecentId(tool.id));
    fetchToolsData(tool);
    setLoading(false);
  };
  // Clicking a card opens the tool at its own URL (shareable + refresh-safe);
  // the effect below reacts to the :id in the URL and opens the modal.
  const handleOpenInfo = (tool) => {
    navigate(`/app/ops-automation-tools/${tool.id}`);
  };
  function closeWithInnerHtml() {
    setModalOpen(false);
    setCustomTool([]);
    setSelectedTool(null);
    navigate("/app/ops-automation-tools");
  }

  // Reset to the first page whenever the search term changes.
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  // Open the tool named in the URL (:id) once the list is loaded — this is what
  // makes a tool link shareable and survive a page refresh / back button.
  useEffect(() => {
    if (!toolData.length) return;
    if (routeToolId) {
      const tool = toolData.find((t) => String(t.id) === String(routeToolId));
      if (
        tool &&
        (!selectedTool || String(selectedTool.id) !== String(routeToolId))
      ) {
        openToolInModal(tool);
      }
    } else if (modalOpen) {
      setModalOpen(false);
      setCustomTool([]);
      setSelectedTool(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeToolId, toolData]);

  // Search (name/description) + alphabetical sort, then paginate.
  const visibleTools = [...toolData]
    .filter((tool) => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return (
        (tool.name || "").toLowerCase().includes(q) ||
        (tool.description || "").toLowerCase().includes(q)
      );
    })
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  const totalPages = Math.max(
    1,
    Math.ceil(visibleTools.length / ITEMS_PER_PAGE)
  );
  const pageSafe = Math.min(currentPage, totalPages);
  const paginatedToolData = visibleTools.slice(
    (pageSafe - 1) * ITEMS_PER_PAGE,
    pageSafe * ITEMS_PER_PAGE
  );

  // "Recently used by you" — map the remembered ids back to current tools
  // (dropping any that no longer exist). Hidden while searching.
  const recentTools = recentIds
    .map((id) => toolData.find((t) => String(t.id) === String(id)))
    .filter(Boolean);
  return (
    <>
      <div className="navView">
        <ToastContainer />
        <Loader loading={loading} />
        <div
          className="component-titleContainer"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          <h2 className="component-title">Ops automation </h2>
          <TextField
            size="small"
            placeholder="Search tools..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            style={{ minWidth: "260px", background: "#fff" }}
          />
        </div>

        {!search.trim() && recentTools.length > 0 && (
          <div style={{ marginTop: "16px" }}>
            <h4 style={{ margin: "0 0 8px", color: "#555", fontWeight: 600 }}>
              Recently used
            </h4>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
              {recentTools.map((tool) => (
                <Card
                  key={tool.id}
                  className="recent-chip"
                  onClick={() => handleOpenInfo(tool)}
                >
                  <CardContent
                    style={{ padding: "10px 16px", paddingBottom: "10px" }}
                  >
                    <b className="tool-title" style={{ fontSize: "13px" }}>
                      {tool.name}
                    </b>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        <Grid container spacing={2} style={{ marginTop: "16px" }}>
          {paginatedToolData.length === 0 ? (
            <Grid item xs={12}>
              <Typography variant="body1">
                {search.trim()
                  ? "No tools match your search."
                  : "No data available."}
              </Typography>
            </Grid>
          ) : (
            paginatedToolData.map((tool, index) => (
              <Grid
                item
                key={index}
                xs={12}
                sm={6}
                md={4}
                lg={3}
                onClick={() => handleOpenInfo(tool)}
              >
                <Card className="card-container">
                  <CardContent>
                    <div className="card-header">
                      <b className="tool-title">{tool.name}</b>
                    </div>
                    {/* <p>Description</p> */}
                    <p style={{ fontSize: "12px" }}>
                      {(tool.description || "").length > 155
                        ? (tool.description || "").substring(0, 155) + "..."
                        : tool.description || ""}
                    </p>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>

        <Dialog
          open={modalOpen}
          onClose={() => closeWithInnerHtml()}
          fullWidth
          maxWidth="md"
          TransitionComponent={Slide}
          transitionDuration={500}
          TransitionProps={{
            direction: "left",
          }}
          PaperProps={{
            style: {
              borderTopLeftRadius: "10px",
              borderTopRightRadius: "10px",
              position: "absolute",
              top: 0,
              right: 0,
            },
          }}
        >
          <IconButton
            style={{
              position: "absolute",
              top: "5px",
              right: "5px",
            }}
            onClick={() => closeWithInnerHtml()}
          >
            <CloseIcon />
          </IconButton>
          {/* Content of the dialog */}
          <DialogContent>
            {selectedTool ? (
              <>
                <div className="tool-header">
                  <h3 className="component-title">{selectedTool.name} </h3>
                  {/* <div className="download-output">
                    <p>{selectedTool.outputTitle}</p>
                    <Button
                      variant="contained"
                      onClick={() => {
                        toast.success("downloading");
                      }}
                      disabled={!tool.outputTitle}
                      disabled
                    >
                      Download
                    </Button> */}
                  {/* </div> */}
                </div>
                <Divider />
                {selectedTool.type === "UI-Python-based" ? (
                  <>
                    {localStorage.setItem(
                      "selectedTool",
                      JSON.stringify(selectedTool)
                    )}{
                      localStorage.setItem("baseUrl",baseUrl)
                    }
                    <InnerHTML html={customTool} />
                  </>
                ) : (
                  <div>
                    <form
                      onSubmit={(event) => handleSubmit(event, selectedTool)}
                    >
                      <div className="formStyle">
                        {[...selectedTool.inputConfigurations]
                          .sort((a, b) => a.index - b.index)
                          .map((input, index) => (
                            <div key={index} className="formItem">
                              {renderInputField(input, index)}
                            </div>
                          ))}
                      </div>
                      <div className="formButtons-block">
                        <Button
                          startIcon={<SaveIcon />}
                          type="submit"
                          variant="contained"
                          sx={{ mt: 2, mb: 2 }}
                          style={{
                            background:
                              "linear-gradient(118deg, #005186, rgb(0 81 134 / 81%))",
                          }}
                        >
                          Save
                        </Button>
                        <Button
                          startIcon={<RotateLeftIcon />}
                          type="reset"
                          variant="contained"
                          sx={{ mt: 2, mb: 2 }}
                          style={{
                            background:
                              "linear-gradient(118deg, #005186, rgb(0 81 134 / 81%))",
                          }}
                        >
                          Reset
                        </Button>
                      </div>
                    </form>

                    <Divider />
                    <div className="tool-info">
                      <h4>Description</h4>
                      <p style={{ fontSize: "12px" }}>
                        {selectedTool.description}
                      </p>
                      <h4>How to use</h4>
                      <p style={{ fontSize: "12px" }}>
                        {selectedTool.instructions}
                      </p>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <Typography variant="body1">No tool selected.</Typography>
            )}
          </DialogContent>
        </Dialog>
        
        <div className="paginationNew" style={{padding:'40px'}}>
          <Pagination
            count={totalPages}
            page={pageSafe}
            onChange={handlePageChange}
            shape="rounded"
          />
        </div>
      </div>
    </>
  );
};

export default ToolList;
